// src/utils/axiosSetup.ts
// Shared axios interceptor setup for consistent 401 handling across all API clients

import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { handleAuthError, shouldRetryAfterAuthError } from './authErrorHandler';

/**
 * Sets up standard interceptors for an axios instance
 * This ensures consistent 401 handling and credential management
 */
export function setupAxiosInterceptors(apiClient: AxiosInstance): void {
  // Request interceptor - ensure credentials are always sent and add JWT token
  apiClient.interceptors.request.use(
    (config) => {
      // Ensure credentials are always included
      config.withCredentials = true;
      
      // Add JWT token from localStorage if available
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Ensure Content-Type is set for POST/PUT requests with JSON data
      if ((config.method === 'post' || config.method === 'put' || config.method === 'patch') && 
          config.data && 
          typeof config.data === 'object' && 
          !(config.data instanceof FormData)) {
        if (!config.headers['Content-Type'] && !config.headers['content-type']) {
          config.headers['Content-Type'] = 'application/json';
        }
      }
      
      // Log request details for debugging authentication issues
      if (config.url?.includes('/review/acknowledge')) {
        console.log('[Axios Interceptor] Review acknowledge request:', {
          url: config.url,
          method: config.method,
          withCredentials: config.withCredentials,
          hasCookies: document.cookie ? 'Yes' : 'No',
          hasToken: !!token,
          headers: config.headers,
        });
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling with 401 retry logic
  apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      const retryCount = originalRequest._retry ? 1 : 0;

      // Handle 401 Unauthorized errors globally
      if (error.response?.status === 401) {
        // Check if we should retry after attempting to refresh auth
        if (shouldRetryAfterAuthError(originalRequest, retryCount)) {
          originalRequest._retry = true;
          
          try {
            // Attempt to refresh the auth session
            const authRefreshed = await handleAuthError(retryCount);
            
            if (authRefreshed) {
              // Auth was refreshed successfully, retry the original request
              console.log('Auth refreshed, retrying original request:', originalRequest.url);
              return apiClient(originalRequest);
            } else {
              // Auth refresh failed - will redirect to login in handleAuthError
              // Create a proper error object for the caller
              const authError = new Error('Authentication required. Please refresh the page and try again.');
              (authError as any).code = 'AUTH_REQUIRED';
              (authError as any).response = error.response;
              (authError as any).status = 401;
              throw authError;
            }
          } catch {
            // Auth refresh failed
            const authError = new Error('Authentication required. Please refresh the page and try again.');
            (authError as any).code = 'AUTH_REQUIRED';
            (authError as any).response = error.response;
            (authError as any).status = 401;
            throw authError;
          }
        } else {
          // Already retried or shouldn't retry - throw auth error
          const authError = new Error('Authentication required. Please refresh the page and try again.');
          (authError as any).code = 'AUTH_REQUIRED';
          (authError as any).response = error.response;
          (authError as any).status = 401;
          throw authError;
        }
      }

      // Log full error response for debugging (except 401s which are handled above)
      console.error('API Error Response:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: originalRequest?.url,
          method: originalRequest?.method,
        }
      });

      // Preserve the original error structure
      const enhancedError: any = error;
      if (error.response) {
        enhancedError.response = error.response;
        enhancedError.status = error.response.status;
        enhancedError.statusText = error.response.statusText;
        enhancedError.data = error.response.data;
      }

      // Transform error response with more detailed information
      const resp = error.response;
      const errData = resp?.data as { message?: string; error?: string; detail?: unknown; errors?: unknown } | undefined;
      if (resp && errData?.message) {
        const newError = new Error(errData.message);
        (newError as any).response = resp;
        (newError as any).status = resp.status;
        (newError as any).statusText = resp.statusText;
        (newError as any).data = resp.data;
        throw newError;
      }
      if (resp && errData?.error) {
        const newError = new Error(errData.error);
        (newError as any).response = resp;
        (newError as any).status = resp.status;
        (newError as any).statusText = resp.statusText;
        (newError as any).data = resp.data;
        throw newError;
      }
      if (resp?.status === 500) {
        const detailMessage = (errData?.detail ?? errData?.errors ?? errData?.message) ?? 'Internal server error';
        const newError = new Error(`Server error: ${String(detailMessage)}`);
        (newError as any).response = resp;
        (newError as any).status = resp.status;
        (newError as any).statusText = resp.statusText;
        (newError as any).data = resp.data;
        throw newError;
      }
      if (resp?.status === 400) {
        const validationErrors = errData?.errors ?? errData?.detail ?? errData?.message;
        if (validationErrors && resp) {
          const errorMessage = typeof validationErrors === 'string'
            ? validationErrors
            : JSON.stringify(validationErrors);
          const newError = new Error(`Validation error: ${errorMessage}`);
          (newError as any).response = resp;
          (newError as any).status = resp.status;
          (newError as any).statusText = resp.statusText;
          (newError as any).data = resp.data;
          throw newError;
        }
      }

      // For other errors, preserve the original error structure
      if (error.response) {
        throw enhancedError;
      }

      if (error.message) {
        throw new Error(error.message);
      }
      throw new Error('An unexpected error occurred');
    }
  );
}

